import { trackUserEvent, wait } from '@vend/utilities'
import { WEB_REGISTER } from '../../../web-register.constant'

const MAX_RETRIES = 3

/**
 * Gets the timeout to wait for a retry.
 * Max's out at 4000ms at 3 retries.
 *
 * @param retryAttempt The number of retries we're up to. e.g. 1, 2, 3, 4...
 * @returns The time to wait before making the attempt
 */
export const getRetryTimeout = (retryAttempt: number): number => {
  if (retryAttempt < 1) return 0
  switch (retryAttempt) {
    case 1: {
      return 1000
    }
    case 2: {
      return 2000
    }
    default: {
      return 4000
    }
  }
}

/**
 * Calls operation and if fails, retries for three times with timeouts of 1s, 2s and 4s
 *
 * @param operation The operation to be retried
 * @returns a Promise resolved by operation
 * @throws error thrown by operation after used up all retry chances
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries = MAX_RETRIES
): Promise<T> => {
  let attemptCounter = 1
  const recursiveRetry = async (): Promise<T> => {
    try {
      return await operation()
    } catch (error) {
      if (attemptCounter > maxRetries) {
        trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Retry LSPAY operation', {
          exceededMaxRetries: true,
          operationName,
        })
        throw error
      }

      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Retry LSPAY operation', {
        attemptCounter,
        operationName,
        // add the stringified error so we can keep a trace of it
        error: '' + error,
      })

      await wait(getRetryTimeout(attemptCounter))
      attemptCounter = attemptCounter + 1
      return recursiveRetry()
    }
  }
  return recursiveRetry()
}
